import Phaser from 'phaser';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import { getCurrentLocale } from '../i18n';

// ── Geometry constants kept in sync with Hand.ts ─────────────────────────────
const MAX_TILES  = 7;
const RACK_PAD_W = 10;   // Hand.ts rackPadW
// Mobile side panel spacing — mirrors OpponentHands.ts SIDE_MARGIN
const SIDE_MARGIN_PX = 8;

// ── Visual constants ─────────────────────────────────────────────────────────
const IDLE_ALPHA  = 0.88;
const HOVER_ALPHA = 1.00;
const HOVER_SCALE = 1.05;
/** Hit area is this multiple of the icon width. */
const HIT_EXPAND  = 1.35;

export interface KnockButtonLayout {
  screenW:    number;
  handY:      number;
  handScale:  number;
  /** > 0 on mobile portrait; 0 on desktop / landscape. Drives placement math. */
  sidePanelW: number;
  unit:       number;
  /** Layout-spec anchor (config/layoutSpec.ts). When provided, the button is
   *  placed exactly here at `iconSize`, bypassing the rack-gap math. */
  anchorX?:   number;
  anchorY?:   number;
  iconSize?:  number;
}

/**
 * KnockButton — a premium in-world "knock on the table" interaction
 * that replaces the PASS button when the local player has no legal moves.
 *
 * Sits on the right side of the rack, between the rack edge and the right
 * cup-holder panel (mobile) or table surface (desktop).
 *
 * Interaction flow:
 *   show() when isMyTurn && noValidMoves && game.active
 *   hide() on any state change that removes those conditions
 *   tap → double-knock animation → onKnock callback
 */
export class KnockButton extends Phaser.GameObjects.Container {
  private _icon: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics;
  private _onKnock: (() => void) | null = null;
  private _animating = false;
  private _isHovered = false;
  /** Displayed icon width in px — updated by setLayout. Hit area derived from this. */
  private _iconSize = 40;
  /** Natural height/width ratio of the PNG — used to preserve aspect ratio. */
  private _iconAspect = 1;
  /** Layout-spec (desktop) mode: the button is a PERSISTENT PASS button —
   *  always visible as pass-inactive, becoming pass-active only when a pass is
   *  required. Set true by setLayout when a spec anchor is provided. In
   *  non-spec (mobile) mode the button keeps the original show/hide behaviour. */
  private _specMode = false;
  /** True while a pass is actually required (button armed / tappable). */
  private _armed = false;

  /** Scene-level pointer listeners — stored so we can remove them on destroy. */
  private _tapListener:   ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private _hoverListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;

  constructor(scene: Phaser.Scene) {
    super(scene, -9999, -9999);   // off-screen until setLayout positions it
    scene.add.existing(this);
    this.setDepth(150);
    this.setVisible(false);

    // Icon — the PASS button defaults to its INACTIVE face (the persistent
    // desktop state); falls back to the legacy knock fist, then a drawn circle
    // so the code runs without any asset. setArmed() swaps to the active face.
    const iconKey = scene.textures.exists(this._passKey(false)) ? this._passKey(false)
                  : scene.textures.exists(this._passKey(true))  ? this._passKey(true)
                  : 'knock-fist';
    if (scene.textures.exists(iconKey)) {
      const img = scene.add.image(0, 0, iconKey);
      img.setOrigin(0.5);
      // Capture natural aspect ratio once so setLayout can preserve proportions.
      if (img.width > 0) this._iconAspect = img.height / img.width;
      this._icon = img;
    } else {
      const g = scene.add.graphics();
      g.fillStyle(0xc9a84c, 1);
      g.fillCircle(0, 0, 20);
      g.lineStyle(1.5, 0xffffff, 0.25);
      g.strokeCircle(0, 0, 20);
      this._icon = g;
    }

    this._icon.setAlpha(IDLE_ALPHA);
    this.add(this._icon);

    // Scene-level tap handler (mirrors Hand.ts _tapRouter — more reliable on mobile).
    this._tapListener = (ptr: Phaser.Input.Pointer) => {
      // Only respond when a pass is actually required. In spec mode the button
      // is always visible but inert until armed; in mobile mode `visible`
      // already implies armed.
      if (!this.visible || this._animating || !this._armed) return;
      if (this._inHitArea(ptr.x, ptr.y)) this._onTap();
    };
    scene.input.on('pointerdown', this._tapListener);

    // Desktop hover: opacity / scale feedback + pointer cursor.
    if (!IS_MOBILE_EXPERIENCE) {
      this._hoverListener = (ptr: Phaser.Input.Pointer) => {
        if (!this.visible || this._animating || !this._armed) return;
        const inside = this._inHitArea(ptr.x, ptr.y);
        if (inside && !this._isHovered) {
          this._isHovered = true;
          this._icon.setAlpha(HOVER_ALPHA);
          this.setScale(HOVER_SCALE);
          scene.game.canvas.style.cursor = 'pointer';
        } else if (!inside && this._isHovered) {
          this._isHovered = false;
          this._icon.setAlpha(IDLE_ALPHA);
          this.setScale(1);
          scene.game.canvas.style.cursor = '';
        }
      };
      scene.input.on('pointermove', this._hoverListener);
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  setOnKnock(cb: () => void): this {
    this._onKnock = cb;
    return this;
  }

  /**
   * Arm / disarm the PASS button.
   *   armed  = a pass is required (my turn, no legal move) → pass-active, tappable
   *   !armed = normal → pass-inactive
   * Spec (desktop) mode keeps the button visible in both states; mobile mode
   * shows it only when armed (original behaviour).
   */
  /** Transform-aware world bounds of the PASS control, or null while it is
   *  still parked off-screen (pre-layout). Read-only geometry accessor
   *  (tutorial spotlight); no gameplay effect. */
  getButtonBounds(): Phaser.Geom.Rectangle | null {
    if (this.x < -5000 || this.y < -5000) return null;
    const b = this.getBounds();
    return b.width > 0 && b.height > 0 ? b : null;
  }

  setArmed(armed: boolean): void {
    this._armed = armed;
    this._isHovered = false;
    if (this._specMode) {
      this._setIcon(this._passKey(armed));
      this._icon.setAlpha(IDLE_ALPHA);
      this.setScale(1);
      this.setVisible(true);
    } else if (armed) {
      this._setIcon(this._passKey(true));
      this.show();
    } else {
      this.hide();
    }
  }

  /** Locale-resolved texture key for the current armed state: Spanish gets
   *  the PASO variants, everything else the English PASS variants. Falls back
   *  to the English key when the Spanish texture is not loaded, so an asset
   *  hiccup can never blank the control. Pure key selection — the icon,
   *  bounds, hit area and armed logic are untouched. */
  private _passKey(armed: boolean): string {
    const es = getCurrentLocale() === 'es-419';
    const key = armed
      ? (es ? 'paso-active'   : 'pass-active')
      : (es ? 'paso-inactive' : 'pass-inactive');
    if (es && !this.scene.textures.exists(key)) {
      return armed ? 'pass-active' : 'pass-inactive';
    }
    return key;
  }

  /** Re-resolve the icon texture for the current locale WITHOUT touching
   *  armed state, visibility, position, size or hit geometry. Called by the
   *  GameScene locale bridge on language change. */
  refreshLocale(): void {
    if (!(this._icon instanceof Phaser.GameObjects.Image)) return;
    this._setIcon(this._passKey(this._armed));
  }

  /** Swap the icon texture (pass-active ↔ pass-inactive) at the current size. */
  private _setIcon(key: string): void {
    if (!(this._icon instanceof Phaser.GameObjects.Image)) return;
    if (!this.scene.textures.exists(key)) return;
    this._icon.setTexture(key);
    if (this._icon.width > 0) this._iconAspect = this._icon.height / this._icon.width;
    this._icon.setDisplaySize(this._iconSize, Math.round(this._iconSize * this._iconAspect));
  }

  /**
   * Position the button and size the icon to fit the available gap.
   * Call after computeLayout and on every handleResize.
   */
  setLayout(layout: KnockButtonLayout): void {
    const { screenW, handY, handScale, sidePanelW, unit } = layout;

    // Layout-spec anchor path (desktop): exact position and size, no gap math.
    // The button is PERSISTENT here — visible from the moment it is laid out,
    // showing its inactive face until a pass is required.
    if (layout.anchorX !== undefined && layout.anchorY !== undefined) {
      this._specMode = true;
      this.setPosition(Math.round(layout.anchorX), Math.round(layout.anchorY));
      this._iconSize = Math.round(layout.iconSize ?? 68);
      this._setIcon(this._passKey(this._armed));
      this.setVisible(true);
      return;
    }

    // Rack geometry (mirrors Hand.ts renderHand constants exactly).
    const tileW   = unit * handScale;
    const spacing = Math.round(4 * handScale);
    const rackW   = MAX_TILES * tileW + (MAX_TILES - 1) * spacing + RACK_PAD_W * 2;
    const rackRightEdge = (screenW + rackW) / 2;

    let cx: number;
    let gapW: number;

    if (sidePanelW > 0) {
      // Mobile portrait — place in the gap between rack and right side panel.
      const panWS    = Math.round(sidePanelW * 0.715);  // mirrors OpponentHands
      const panLeftX = screenW - SIDE_MARGIN_PX - panWS;
      gapW = panLeftX - rackRightEdge;
      cx   = rackRightEdge + gapW / 2;
    } else {
      // Desktop / landscape — place comfortably to the right of the rack.
      gapW = 80;
      cx   = rackRightEdge + gapW / 2;
    }

    this.setPosition(Math.round(cx), Math.round(handY));

    // Icon width: 95% of available gap, clamped to [36, 68] px.
    this._iconSize = Math.max(36, Math.min(68, Math.round(gapW * 0.95)));

    if (this._icon instanceof Phaser.GameObjects.Image) {
      // Size by width; derive height from the texture's natural aspect ratio so
      // the fist is never stretched or squished.  The asset has negligible
      // transparent padding (~3%) so no overdraw compensation is needed.
      this._icon.setDisplaySize(
        this._iconSize,
        Math.round(this._iconSize * this._iconAspect),
      );
    }
  }

  show(): void {
    if (this._animating) return;
    this._isHovered = false;
    this._icon.setAlpha(IDLE_ALPHA);
    this.setScale(1);
    this.setVisible(true);
  }

  hide(): void {
    this.setVisible(false);
    this._animating = false;
    this._isHovered = false;
    if (this.scene?.game?.canvas) {
      this.scene.game.canvas.style.cursor = '';
    }
  }

  destroy(fromScene?: boolean): void {
    this.hide();
    if (this.scene?.input) {
      if (this._tapListener)   this.scene.input.off('pointerdown', this._tapListener);
      if (this._hoverListener) this.scene.input.off('pointermove',  this._hoverListener);
    }
    this._tapListener   = null;
    this._hoverListener = null;
    super.destroy(fromScene);
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private _inHitArea(wx: number, wy: number): boolean {
    // Hit area is sized against _iconSize (visible fist), not the overdrawn texture.
    const half = (this._iconSize * HIT_EXPAND) / 2;
    return Math.abs(wx - this.x) <= half && Math.abs(wy - this.y) <= half;
  }

  private _onTap(): void {
    if (this._animating) return;
    this._animating = true;
    this._icon.setAlpha(HOVER_ALPHA);
    // Reset container transforms before animating.
    this.setScale(1);
    this.setAngle(0);
    this._playDoubleKnock(() => {
      this._animating = false;
      // Spec mode: the button stays on the table, reverting to its inactive
      // face. Mobile: hide as before. (The subsequent turn change also fires
      // setArmed(false); both are idempotent.)
      if (this._specMode) {
        this.setArmed(false);
      } else {
        this.hide();
      }
      this._onKnock?.();
    });
  }

  /**
   * Subtle knock — lightly tapping a wooden domino table.
   *
   * IMPORTANT: tweens target the Container (this), NOT the icon child.
   * The icon's scaleX/scaleY are owned by setDisplaySize(); overwriting
   * them via a tween would snap the image to its native 496×661 size.
   *
   * Sequence (total ~180ms):
   *   Down:   y +2px, scale 0.97, rotate -0.5°, 60ms
   *   Return: y 0,    scale 1.00, rotate  0.0°, 80ms
   *   Hold:   40ms at rest position → onComplete (hide + pass)
   */
  private _playDoubleKnock(onComplete: () => void): void {
    if (!this.scene?.tweens) { onComplete(); return; }

    const restY = this.y;

    this.scene.tweens.chain({
      targets: this,
      tweens: [
        { y: restY + 2, scaleX: 0.97, scaleY: 0.97, angle: -0.5, duration: 60, ease: 'Cubic.Out' },
        { y: restY,     scaleX: 1.00, scaleY: 1.00, angle:  0.0, duration: 80, ease: 'Cubic.In'  },
        { y: restY,     duration: 40, onComplete },
      ],
    });
  }
}
